export interface TimeSettings {
  dayStartMinutes: number;
  defaultDailyHours: number;
  snapMinutes: number;
}

export interface TimeEntry {
  id: string;
  date: string;
  startMinutes: number;
  endMinutes: number;
  workNumber: string;
  description: string;
  normalHours: string;
  ur: string;
  hal: string;
  overtime50: string;
  overtime100: string;
  su: string;
  pvr: string;
  rr: string;
  kilometres: string;
  submitted: boolean;
}

export const DEFAULT_TIME_SETTINGS: TimeSettings = {
  dayStartMinutes: 7 * 60,
  defaultDailyHours: 8,
  snapMinutes: 15,
};

export const MIN_BLOCK_MINUTES = 15;

export function clampMinutes(value: number) {
  return Math.max(0, Math.min(24 * 60, value));
}

export function snapMinutes(value: number, step = DEFAULT_TIME_SETTINGS.snapMinutes) {
  return clampMinutes(Math.round(value / step) * step);
}

export function formatClock(minutes: number) {
  const value = clampMinutes(minutes);
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

export function parseClock(value: string) {
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return clampMinutes(hours * 60 + minutes);
}

export function durationHours(entry: Pick<TimeEntry, "startMinutes" | "endMinutes">) {
  return Math.max(0, entry.endMinutes - entry.startMinutes) / 60;
}

export function durationLabel(entry: Pick<TimeEntry, "startMinutes" | "endMinutes">) {
  const minutes = Math.max(0, entry.endMinutes - entry.startMinutes);
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
}

function numberText(value: number) {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}

function usesOnlyNormalHours(entry: TimeEntry) {
  return [entry.ur, entry.hal, entry.overtime50, entry.overtime100, entry.su]
    .every((value) => !value || Number(value) === 0);
}

function syncDuration(entry: TimeEntry, startMinutes: number, endMinutes: number) {
  const oldDuration = durationHours(entry);
  const normal = Number(entry.normalHours);
  const shouldSync = usesOnlyNormalHours(entry) && (!entry.normalHours || Math.abs(normal - oldDuration) < 0.001);
  return {
    ...entry,
    startMinutes,
    endMinutes,
    normalHours: shouldSync ? numberText((endMinutes - startMinutes) / 60) : entry.normalHours,
  };
}

export function createTimeEntry(
  date: string,
  index: number,
  settings: TimeSettings = DEFAULT_TIME_SETTINGS,
): TimeEntry {
  const endMinutes = Math.min(24 * 60, settings.dayStartMinutes + settings.defaultDailyHours * 60);
  return {
    id: `time-${index}`,
    date,
    startMinutes: settings.dayStartMinutes,
    endMinutes,
    workNumber: "1048-21",
    description: "",
    normalHours: numberText((endMinutes - settings.dayStartMinutes) / 60),
    ur: "",
    hal: "",
    overtime50: "",
    overtime100: "",
    su: "",
    pvr: "",
    rr: "",
    kilometres: "",
    submitted: false,
  };
}

export function normalizeTimeEntry(
  entry: Partial<TimeEntry> & Pick<TimeEntry, "id" | "date">,
  settings: TimeSettings = DEFAULT_TIME_SETTINGS,
): TimeEntry {
  const fallbackHours = Number(entry.normalHours) || settings.defaultDailyHours;
  const startMinutes = Number.isFinite(entry.startMinutes) ? Number(entry.startMinutes) : settings.dayStartMinutes;
  const endMinutes = Number.isFinite(entry.endMinutes)
    ? Number(entry.endMinutes)
    : Math.min(24 * 60, startMinutes + fallbackHours * 60);
  return {
    ...createTimeEntry(entry.date, 0, settings),
    ...entry,
    startMinutes,
    endMinutes,
    ur: entry.ur ?? "",
    hal: entry.hal ?? "",
    overtime100: entry.overtime100 ?? "",
    su: entry.su ?? "",
    pvr: entry.pvr ?? "",
    rr: entry.rr ?? "",
  };
}

export function resizeDayEdge(
  entries: TimeEntry[],
  id: string,
  edge: "start" | "end",
  nextMinute: number,
) {
  return entries.map((entry) => {
    if (entry.id !== id) return entry;
    const minute = snapMinutes(nextMinute);
    if (edge === "start") {
      const startMinutes = Math.min(minute, entry.endMinutes - MIN_BLOCK_MINUTES);
      return syncDuration(entry, startMinutes, entry.endMinutes);
    }
    const endMinutes = Math.max(minute, entry.startMinutes + MIN_BLOCK_MINUTES);
    return syncDuration(entry, entry.startMinutes, endMinutes);
  });
}

export function resizeBoundary(
  entries: TimeEntry[],
  leftId: string,
  rightId: string,
  nextMinute: number,
) {
  const left = entries.find((entry) => entry.id === leftId);
  const right = entries.find((entry) => entry.id === rightId);
  if (!left || !right || left.date !== right.date) return entries;
  const minute = Math.max(
    left.startMinutes + MIN_BLOCK_MINUTES,
    Math.min(snapMinutes(nextMinute), right.endMinutes - MIN_BLOCK_MINUTES),
  );
  return entries.map((entry) => {
    if (entry.id === leftId) return syncDuration(entry, entry.startMinutes, minute);
    if (entry.id === rightId) return syncDuration(entry, minute, entry.endMinutes);
    return entry;
  });
}

export function moveTimeEntry(
  entries: TimeEntry[],
  id: string,
  nextStartMinute: number,
) {
  const target = entries.find((entry) => entry.id === id);
  if (!target) return entries;
  const dayEntries = entries
    .filter((entry) => entry.date === target.date)
    .sort((a, b) => a.startMinutes - b.startMinutes);
  const index = dayEntries.findIndex((entry) => entry.id === id);
  if (index < 0) return entries;
  const duration = target.endMinutes - target.startMinutes;
  const previousEnd = dayEntries[index - 1]?.endMinutes ?? 0;
  const nextStart = dayEntries[index + 1]?.startMinutes ?? 24 * 60;
  const startMinutes = Math.max(
    previousEnd,
    Math.min(snapMinutes(nextStartMinute), nextStart - duration),
  );
  return entries.map((entry) =>
    entry.id === id
      ? syncDuration(entry, startMinutes, startMinutes + duration)
      : entry);
}

export function splitTimeEntry(entries: TimeEntry[], id: string, nextId: string) {
  const target = entries.find((entry) => entry.id === id);
  if (!target || target.endMinutes - target.startMinutes < MIN_BLOCK_MINUTES * 2) return entries;
  return splitTimeEntryAt(entries, id, nextId, (target.startMinutes + target.endMinutes) / 2);
}

export function splitTimeEntryAt(
  entries: TimeEntry[],
  id: string,
  nextId: string,
  nextMinute: number,
) {
  const target = entries.find((entry) => entry.id === id);
  if (!target || target.endMinutes - target.startMinutes < MIN_BLOCK_MINUTES * 2) return entries;
  const middle = snapMinutes(nextMinute);
  const splitPoint = Math.max(
    target.startMinutes + MIN_BLOCK_MINUTES,
    Math.min(middle, target.endMinutes - MIN_BLOCK_MINUTES),
  );
  const left = syncDuration(target, target.startMinutes, splitPoint);
  const right = {
    ...syncDuration(target, splitPoint, target.endMinutes),
    id: nextId,
    submitted: false,
  };
  const result: TimeEntry[] = [];
  for (const entry of entries) {
    if (entry.id === id) result.push(left, right);
    else result.push(entry);
  }
  return result;
}

export function mergeTimeEntries(
  entries: TimeEntry[],
  leftId: string,
  rightId: string,
  keepId: string,
) {
  const leftIndex = entries.findIndex((entry) => entry.id === leftId);
  const rightIndex = entries.findIndex((entry) => entry.id === rightId);
  if (leftIndex < 0 || rightIndex < 0 || rightIndex !== leftIndex + 1) return entries;
  const left = entries[leftIndex];
  const right = entries[rightIndex];
  if (left.date !== right.date || left.endMinutes !== right.startMinutes) return entries;
  const kept = keepId === leftId ? left : keepId === rightId ? right : null;
  if (!kept) return entries;
  const merged = syncDuration(kept, left.startMinutes, right.endMinutes);
  return [
    ...entries.slice(0, leftIndex),
    merged,
    ...entries.slice(rightIndex + 1),
  ];
}

export function applySettingsToDraftDays(entries: TimeEntry[], settings: TimeSettings) {
  const counts = entries.reduce<Record<string, number>>((result, entry) => {
    result[entry.date] = (result[entry.date] ?? 0) + 1;
    return result;
  }, {});
  return entries.map((entry) => {
    if (entry.submitted || counts[entry.date] !== 1) return entry;
    const endMinutes = Math.min(24 * 60, settings.dayStartMinutes + settings.defaultDailyHours * 60);
    return syncDuration(entry, settings.dayStartMinutes, endMinutes);
  });
}

export function entryHourTotal(entry: TimeEntry) {
  return [entry.normalHours, entry.ur, entry.hal, entry.overtime50, entry.overtime100, entry.su]
    .reduce((sum, value) => sum + (Number(value) || 0), 0);
}
